import cookie from 'js-cookie';
import queryString from 'query-string';

import warehouse from 'services/warehouse';
import analytics from 'services/analytics';

import { Product } from 'redux/models';
import {
  getIsSharedSpace,
  studioUpdateProduct
} from 'redux/Studio';

import { toggleAddToCartFeedback as refactoredToggleAddToCartFeedback } from 'shared/components/AddToCartFeedback/state';

import { ShoppingCart } from 'shared/components/Checkout/models';

const SET_IS_FETCHING = 'CHECKOUT/SET_IS_FETCHING';
const ITEM_TYPE_PRODUCT = 26;

// =================
// UTILITY FUNCTIONS
// =================
/** Returns 2 digit current month. */
const monthInitial = () => {
  const monthInt = new Date().getMonth() + 1;
  return monthInt < 10 ? `0${monthInt}` : `${monthInt}`;
};

/** Returns state defaults per country. */
const stateDefaultsFor = (country) => {
  const stateRequired = country === 'US' || country === 'CA';
  const newState = country === 'US'
    ? 'CA'
    : country === 'CA' ? 'BC' : '';

  return { stateRequired, newState };
};

// =======
// REDUCER
// =======
const initialState = {
  // the user's current shopping cart
  cart: null,
  isFetchingCart: false,

  // There ends up being multiple actions/events
  // where we need to hit the server to update the
  // cart. While that update is in progress we want to
  // show a spinner on the cart total. Thus, decided to
  // elevate the cart-is-updating state to redux so it can
  // be accessed from any connected component. For similar
  // reasons, also adding cart-update-error state to redux.
  cartUpdating: false,
  cartUpdateError: null,

  // applied promo code
  // if not empty, its a valid promo code
  // otherwise will be empty string
  appliedPromoCode: '',

  // component state
  openForm: 'shipping', // one of 'shipping', 'payment', 'billing'
  mode: 'edit', // one of 'edit', 'review', 'submitting'

  // shipping address
  shippingFormFields: {
    addressName: '',
    phoneNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    stateRequired: true
  },

  validShippingFormFields: {
    city: '',
    country: '',
    postal: '',
    street: '',
    state: ''
  },

  // billing address
  billingFormFields: {
    sameAsShipping: false,
    addressName: '',
    phoneNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    stateRequired: true
  },

  // payment options, one of: 'cc', 'paypal', 'affirm'
  selectedPaymentOption: 'cc',

  // payments credit cart (stripe)
  selectedCreditCard: null, // if null and selectedPaymentOption === 'cc', implies new CC
  savedCreditCards: [], // array of objects with fields: { id, type, expireMonth, expireYear, last4 }
  creditCardFormFields: {
    cardName: '',
    cardNumber: '',
    expireMonth: monthInitial(),
    expireYear: `${new Date().getFullYear()}`,
    cvc: '',
    saveCC: false
  },

  // Order metadata for last successful order.
  orderInfo: {
    orderNumber: '',
    total: '',
    paymentMethod: ''
  },
  stripeToken: {},

  // Error from click on review order (Merch checkout).
  reviewOrderError: null
};

export const checkoutReducerKey = 'checkout';
export const checkoutReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'CHECKOUT/APPLY_PROMO_CODE': {
      const { appliedPromoCode } = action.payload;
      return { ...state, appliedPromoCode };
    }

    case 'CHECKOUT/SET_CART':
      return {
        ...state,
        cart: action.payload,
        reviewOrderError: null
      };

    case SET_IS_FETCHING:
      return { ...state, isFetchingCart: action.payload };

    case 'CHECKOUT/SET_CART_UPDATING': {
      const cartUpdating = action.payload;
      const cartUpdateError = cartUpdating ? null : state.cartUpdateError;
      return { ...state, cartUpdating, cartUpdateError };
    }

    case 'CHECKOUT/SET_CC_FORM_FIELD_ERROR':
      return { ...state, creditCardFormFieldError: action.payload };

    case 'CHECKOUT/SET_CART_UPDATE_ERROR':
      return { ...state, cartUpdateError: action.payload };

    case 'CHECKOUT/SET_OPEN_FORM':
      return { ...state, openForm: action.payload };

    case 'CHECKOUT/SET_MODE':
      return { ...state, mode: action.payload };

    case 'CHECKOUT/SET_SHIPPING_FORM_FIELD': {
      const { fieldName, value } = action.payload;
      return {
        ...state,
        shippingFormFields: {
          ...state.shippingFormFields,
          [fieldName]: value
        }
      };
    }

    case 'CHECKOUT/SET_SHIPPING_COUNTRY': {
      const country = action.payload;
      const { stateRequired, newState } = stateDefaultsFor(country);
      return {
        ...state,
        shippingFormFields: {
          ...state.shippingFormFields,
          country,
          state: newState,
          stateRequired
        }
      };
    }

    case 'CHECKOUT/SET_BILLING_FORM_FIELD': {
      const { fieldName, value } = action.payload;
      return {
        ...state,
        billingFormFields: {
          ...state.billingFormFields,
          [fieldName]: value
        }
      };
    }

    case 'CHECKOUT/SET_BILLING_COUNTRY': {
      const country = action.payload;
      const { stateRequired, newState } = stateDefaultsFor(country);
      return {
        ...state,
        billingFormFields: {
          ...state.billingFormFields,
          country,
          state: newState,
          stateRequired
        }
      };
    }

    case 'CHECKOUT/TOGGLE_BILLING_SAME':
      return {
        ...state,
        billingFormFields: {
          ...state.billingFormFields,
          sameAsShipping: !state.billingFormFields.sameAsShipping
        }
      };

    case 'CHECKOUT/SELECT_PAYMENT_OPTION':
      return { ...state, selectedPaymentOption: action.payload };

    case 'CHECKOUT/SELECT_CARD':
      return { ...state, selectedCreditCard: action.payload };

    case 'CHECKOUT/SET_SAVED_CARDS': {
      const savedCreditCards = action.payload;

      // Make sure selected CC is one of new saved CC.
      // If not, select the first saved CC.
      let selectedCreditCard = state.selectedCreditCard;
      if (selectedCreditCard) {
        selectedCreditCard = savedCreditCards.find(
          cc => cc.id === selectedCreditCard.id
        ) || savedCreditCards[0];
      }

      return { ...state, savedCreditCards, selectedCreditCard };
    }

    case 'CHECKOUT/SET_CC_FORM_FIELD': {
      const { fieldName, value } = action.payload;
      return {
        ...state,
        creditCardFormFields: {
          ...state.creditCardFormFields,
          [fieldName]: value
        }
      };
    }

    case 'CHECKOUT/ORDER_SUCCESS':
      return {
        ...state,
        cart: ShoppingCart.defaultEmptyCart,
        orderInfo: action.payload,
        appliedPromoCode: ''
      };

    case 'CHECKOUT/SET_STRIPE_TOKEN':
      return {
        ...state,
        stripeToken: action.payload
      };

    case 'CHECKOUT/SET_VALID_ADDRESS':
      const { city, country, state: addressState, street, postal } = action.payload || {};
      return {
        ...state,
        validShippingFormFields: {
          ...state.validShippingFormFields,
          city,
          country,
          state: addressState,
          street,
          postal
        },
        validAddressError: ''
      };

    case 'CHECKOUT/USE_VALID_ADDRESS':
      const { city: validCity, country: validCountry, state: validState, street: validStreet, postal: validPostal } = action.payload || {};
      return {
        ...state,
        shippingFormFields: {
          ...state.shippingFormFields,
          streetAddress: validStreet,
          city: validCity,
          state: validState,
          zip: validPostal,
          country: validCountry
        },
        validAddressError: ''
      };

    case 'CHECKOUT/NO_VALID_ADDRESS':
      return { ...state, validAddressError: action.payload };

    case 'CHECKOUT/REVIEW_ORDER_ERROR':
      return { ...state, reviewOrderError: action.payload };

    default:
      return state;
  }
};

// =========
// SELECTORS
// =========
const ownSlice = state => state[checkoutReducerKey];

export const getAppliedPromoCode = state => {
  return ownSlice(state).appliedPromoCode;
};

export const getOpenForm = state => {
  return ownSlice(state).openForm;
};

export const getMode = state => {
  return ownSlice(state).mode;
};

export const getSuggestedAddress = state => {
  return ownSlice(state).validShippingFormFields;
};

export const getShippingFormFields = state => {
  return ownSlice(state).shippingFormFields;
};

export const getBillingFormFields = state => {
  return ownSlice(state).billingFormFields;
};

export const getBillingSameAsShipping = state => {
  return getBillingFormFields(state).sameAsShipping;
};

export const getSelectedPaymentOption = state => {
  return ownSlice(state).selectedPaymentOption;
};

export const getSelectedPaymentState = state => {
  let data = null;
  const option = getSelectedPaymentOption(state);
  switch (option) {
    case 'cc':
      data = {
        saved: getSavedCreditCards(state),
        selected: getSelectedCreditCard(state),
        formFields: getCreditCardFormFields(state),
        formFieldError: getCreditCardFormFieldError(state)
      };
      break;
    case 'affirm':
      data = {
        shippingCountry: getShippingFormFields(state).country
      };
      break;
    case 'paypal':
    default:
      break;
  }

  return { selected: option, data };
};

export const getSavedCreditCards = state => {
  return ownSlice(state).savedCreditCards;
};

export const getSelectedCreditCard = state => {
  return ownSlice(state).selectedCreditCard;
};

export const getCreditCardFormFields = state => {
  return ownSlice(state).creditCardFormFields;
};

export const getCreditCardFormFieldError = state => {
  return ownSlice(state).creditCardFormFieldError;
};

export const getValidAddressError = state => {
  return ownSlice(state).validAddressError;
};

export const getReviewOrderError = state => {
  return ownSlice(state).reviewOrderError;
};

export const getStripeToken = state => {
  return ownSlice(state).stripeToken;
};

/**
 * Gets the current shopping cart.
 * Will return an empty shopping cart if one not yet received from server.
 */
export const getShoppingCart = state => {
  const data = ownSlice(state);
  return data.cart || ShoppingCart.defaultEmptyCart;
};

export const getIsFetchingCart = state => {
  return ownSlice(state).isFetchingCart;
};

export const getCartUpdateStatus = state => {
  const { cartUpdating, cartUpdateError } = ownSlice(state);
  return {
    updating: cartUpdating,
    error: cartUpdateError
  };
};

export const getOrderTotal = state => {
  const cart = getShoppingCart(state);
  return cart.totalOrderAmount();
};

/**
 * Returns the order information for the last
 * successful order in this session.
 */
export const getOrderInfo = state => {
  const data = ownSlice(state);
  return data.orderInfo;
};

/**
 * Checkout screen has a legacy design with a custom
 * small screen breakpoint.
 */
export const getIsSmallScreen = state => {
  return state.device.screenWidth <= 860;
};

// ================
// ACTIONS / THUNKS
// ================
export const setOpenForm = (formName) => ({
  type: 'CHECKOUT/SET_OPEN_FORM',
  payload: formName
});

export const setMode = (mode) => ({
  type: 'CHECKOUT/SET_MODE',
  payload: mode
});

export const setShippingAddressFormField = (fieldName, value) => (dispatch, getState) => {
  if (fieldName !== 'country') {
    return dispatch({
      type: 'CHECKOUT/SET_SHIPPING_FORM_FIELD',
      payload: { fieldName, value }
    });
  }

  // Handle country.
  const shipping = getShippingFormFields(getState());
  if (value === shipping.country) { return; } // handle no change
  dispatch({
    type: 'CHECKOUT/SET_SHIPPING_COUNTRY',
    payload: value
  });
};

export const toggleBillingSameAsShipping = () => ({
  type: 'CHECKOUT/TOGGLE_BILLING_SAME'
});

export const useValidAddressAsShipping = (validAddress) => ({
  type: 'CHECKOUT/USE_VALID_ADDRESS',
  payload: validAddress
});

export const setBillingAddressFormField = (fieldName, value) => (dispatch, getState) => {
  if (fieldName !== 'country') {
    return dispatch({
      type: 'CHECKOUT/SET_BILLING_FORM_FIELD',
      payload: { fieldName, value }
    });
  }

  // Handle country.
  const billing = getBillingFormFields(getState());
  if (value === billing.country) { return; } // handle no change
  dispatch({
    type: 'CHECKOUT/SET_BILLING_COUNTRY',
    payload: value
  });
};

export const selectPaymentOption = (option) => ({
  type: 'CHECKOUT/SELECT_PAYMENT_OPTION',
  payload: option
});

export const selectCreditCart = (card) => ({
  type: 'CHECKOUT/SELECT_CARD',
  payload: card
});

export const setCreditCartFormFieldError = (error) => ({
  type: 'CHECKOUT/SET_CC_FORM_FIELD_ERROR',
  payload: error
});

export const setCreditCartFormField = (fieldName, value) => ({
  type: 'CHECKOUT/SET_CC_FORM_FIELD',
  payload: { fieldName, value }
});

export const setStripeToken = (token) => ({
  type: 'CHECKOUT/SET_STRIPE_TOKEN',
  payload: token
});

/**
 * Update saved credit card state.
 */
let autoSelectedSavedCCOnce = false;

const updateSavedCreditCardsFromWHCartResponse = (savedCCs) => dispatch => {
  const savedCreditCards = savedCCs
    .map(cc => {
      const tag = cc.card_tag;
      if (!cc.id || !tag || tag.length < 4) { return null; }

      return {
        id: cc.id,
        type: tag[0],
        expireMonth: tag[1],
        expireYear: tag[2],
        last4: tag[3]
      };
    })
    .filter(card => !!card);

  if (savedCreditCards.length > 0) {
    dispatch({
      type: 'CHECKOUT/SET_SAVED_CARDS',
      payload: savedCreditCards
    });

    // Auto select the first saved credit card.
    // But only do this once so we do not clear user selection.
    if (!autoSelectedSavedCCOnce) {
      autoSelectedSavedCCOnce = true;
      dispatch(selectCreditCart(savedCreditCards[0]));
    }
  }
};

/**
 * Updates the cart-is-updating state.
 * When set to true, the cart-update-error
 * state is also cleared.
 */
const setCartUpdating = (isUpdating) => ({
  type: 'CHECKOUT/SET_CART_UPDATING',
  payload: isUpdating
});

/**
 * Actions for clearing and setting the cart update error.
 */
export const setCartUpdateError = (error) => ({
  type: 'CHECKOUT/SET_CART_UPDATE_ERROR',
  payload: error
});

export const clearCartUpdateError = () => {
  return setCartUpdateError(null);
};

/**
* Note: This is used for the Package & PPO checkouts ("Review Order" CTA).
* Merck Checkout uses updateCart ("Review Order" CTA).

* Actions for adding, deleting, and updating cart Products.
* For these actions we should require a successful server
* response to continue with cart update, i.e., we should
* ensure Cart is strictly consistent on both client and server.
*
* This means calling code should be catching Promise errors
* and handling in UI with feedback to user.
*/
const updateCartWithBillingInfo = ({ currentCart = {} }) => async (dispatch, getState) => {
  dispatch(setCartUpdating(true));

  try {
    let requestData = {
      code: getAppliedPromoCode(getState()),
      orderItems: currentCart.toOrderItems()
    };

    const billingInfo = getAddressInfo(getBillingFormFields(getState()));
    if (billingInfo) {
      requestData.billingInfo = billingInfo;
    }

    // HACK: we need to include spaceID with cart updates to trigger
    // abandonded cart emails. The spaceID should really come from
    // shoppable studio state but storing that in state is still to
    // be implemented. Here, we only need to worry about adding
    // space_id to requestData when we are updating the studio cart
    // so we can parse the pathname.
    let spaceId = null;
    const pathElements = window.location.pathname.split('/');
    if (pathElements.length >= 3 && pathElements[1] === 'studio') {
      spaceId = parseInt(pathElements[2], 10);
    }

    if (spaceId) {
      requestData.space_id = spaceId;
    }

    let sharedSpace = getIsSharedSpace();
    if (sharedSpace) {
      requestData.shared = true;
    }

    const whCart = await warehouse.updateShoppingCart(requestData);
    const cart = ShoppingCart.createShoppingCartFromWarehouseResponse(whCart);

    // Dispatch warnings to display if present
    if (whCart.warning) {
      dispatch(setCartUpdateError(whCart.warning));
    }

    // For some reason, saved credit cards are returned
    // in the cart response. So handle that here.
    const savedCCs = whCart.previousCreditsCards;
    if (savedCCs && savedCCs.length > 0) {
      dispatch(updateSavedCreditCardsFromWHCartResponse(savedCCs));
    }

    // Add cart to state.
    return dispatch({
      type: 'CHECKOUT/SET_CART',
      payload: cart
    });
  } finally {
    dispatch(setCartUpdating(false));
  }
};

/**
 * Note: This is used for the Merck Checkout ("Review Order" CTA).
 * Package & PPO checkouts uses updateCartWithBillingInfo ("Review Order" CTA).
 *
 * Actions for adding, deleting, and updating cart Products.
 * For these actions we should require a successful server
 * response to continue with cart update, i.e., we should
 * ensure Cart is strictly consistent on both client and server.
 *
 * This means calling code should be catching Promise errors
 * and handling in UI with feedback to user.
 */
const updateCart = (
  updatedCart,
  useProductReviewApi = false,
  action = 'update', // used in V2 Cart APIs
  product = null
) => async (dispatch, getState) => {
  // Updating cart is NOT allowed when there is a user id override.
  // Admin view of customer's cart is READ ONLY right now.
  const userIdOverride = queryString.parse(window.location.search).userId;
  if (userIdOverride) {
    return;
  }

  dispatch(setCartUpdating(true));

  try {
    let requestData = {
      code: getAppliedPromoCode(getState()),
      orderItems: updatedCart.toOrderItems()
    };

    const shippingInfo = getAddressInfo(getShippingFormFields(getState()));
    if (shippingInfo) {
      requestData.shippingInfo = shippingInfo;
    }

    // HACK: we need to include spaceID with cart updates to trigger
    // abandonded cart emails. The spaceID should really come from
    // shoppable studio state but storing that in state is still to
    // be implemented. Here, we only need to worry about adding
    // space_id to requestData when we are updating the studio cart
    // so we can parse the pathname.
    let spaceId = null;
    const pathElements = window.location.pathname.split('/');
    if (pathElements.length >= 3 && pathElements[1] === 'studio') {
      spaceId = parseInt(pathElements[2], 10);
    }

    if (spaceId) {
      requestData.space_id = spaceId;
    }

    let sharedSpace = getIsSharedSpace();
    if (sharedSpace) {
      requestData.shared = true;
    }

    let whCart = null;
    if (action === 'remove') {
      requestData = {
        ...requestData,
        orderItems: [{ // we only need to send the product to be removed
          productId: product.id(),
          subtotal: 0,
          type: ITEM_TYPE_PRODUCT,
          qty: 0,
          extra: {}
        }]
      };
      whCart = await warehouse.removeProductFromCart(requestData);
    } else if (action === 'add') {
      const orderItemProduct = requestData.orderItems.find(oi => oi.productId === product.id());
      requestData = {
        ...requestData,
        orderItems: [orderItemProduct] // we only need to send the product to be added or updated
      };
      whCart = await warehouse.addProductToCart(requestData);
    } else if (useProductReviewApi) {
      whCart = await warehouse.reviewShoppingCartProducts(requestData);
    } else {
      whCart = await warehouse.updateShoppingCart(requestData);
    }

    const cart = ShoppingCart.createShoppingCartFromWarehouseResponse(whCart);

    // Dispatch warnings to display if present
    if (whCart.warning) {
      dispatch(setCartUpdateError(whCart.warning));
    }

    // For some reason, saved credit cards are returned
    // in the cart response. So handle that here.
    const savedCCs = whCart.previousCreditsCards;
    if (savedCCs && savedCCs.length > 0) {
      dispatch(updateSavedCreditCardsFromWHCartResponse(savedCCs));
    }

    // Add cart to state.
    return dispatch({
      type: 'CHECKOUT/SET_CART',
      payload: cart
    });
  } catch (error) {
    if (useProductReviewApi) {
      return dispatch({
        type: 'CHECKOUT/REVIEW_ORDER_ERROR',
        payload: error
      });
    } else {
      // Rethrow shopping cart update errors. Error handling is handled upstream.
      throw error;
    }
  } finally {
    dispatch(setCartUpdating(false));
  }
};

export const addProductToCart = ({ product, quantity, designId, productHotspotId, retries = 1 }) => async (dispatch, getState) => {
  // don't add products without valid prices
  if (!product || !product.currentPrice() || !product.regularPrice()) { return; }
  const currentCart = getShoppingCart(getState());
  const updatedCart = currentCart.addProduct({ product, quantity, designId, productHotspotId });
  const useProductReviewApi = false;
  const action = 'add';
  await dispatch(updateCart(updatedCart, useProductReviewApi, action, product));
  const added = getShoppingCart(getState()).isProductInCart(product);
  if (added) {
    if (designId) {
      dispatch(refactoredToggleAddToCartFeedback(product));
    }
    return Promise.resolve();
  }

  // If the Product price changes, adding Product to cart will fail silently.
  // If this happens, we should get the latest Product info and retry.
  if (retries > 0) {
    const whProduct = await warehouse.getProductById(product.id());
    const updatedProduct = Product.createProductFromWarehouseProductResponse({ whProduct });
    dispatch(studioUpdateProduct(updatedProduct));

    return dispatch(addProductToCart({
      product: updatedProduct,
      quantity,
      designId,
      productHotspotId,
      retries: --retries
    }));
  }

  // This means ATC failed.
  // TODO: bugsnag
  return Promise.resolve();
};

export const deleteProductFromCart = (product) => (dispatch, getState) => {
  const currentCart = getShoppingCart(getState());
  const updatedCart = currentCart.deleteProduct(product);
  const useProductReviewApi = false;
  const action = 'remove';
  return dispatch(updateCart(updatedCart, useProductReviewApi, action, product));
};

export const updateProductCartQuantity = (product, quantity, retries = 1) => async (dispatch, getState) => {
  const currentCart = getShoppingCart(getState());
  const updatedCart = currentCart.updateProductQuantity(product, quantity);
  const useProductReviewApi = false;
  const action = 'add'; // the behavior on BE is add-or-update
  await dispatch(updateCart(updatedCart, useProductReviewApi, action, product));

  // See if quantity update worked
  const cartProduct = getShoppingCart(getState()).getProductItemFor(product);
  if (cartProduct && cartProduct.quantity() === quantity) {
    return Promise.resolve();
  }

  // Its possible that a product price is updated after it has been
  // added to the cart. When that occurs, the BE will return the updated
  // product price but fails to update the quantity. Thus, we should just
  // go ahead and try the quantity update again if it fails first time.
  if (retries > 0) {
    const whProduct = await warehouse.getProductById(product.id());
    const updatedProduct = Product.createProductFromWarehouseProductResponse({ whProduct });
    dispatch(studioUpdateProduct(updatedProduct));

    return dispatch(updateProductCartQuantity(updatedProduct, quantity, --retries));
  }

  // This means update item quantity failed.
  // TODO: bugsnag
  return Promise.resolve();
};

export const applyPromoCode = (promoCode) => async (dispatch, getState) => {
  try {
    // Save the promoCode to state.
    dispatch({
      type: 'CHECKOUT/APPLY_PROMO_CODE',
      payload: { appliedPromoCode: promoCode }
    });

    // Update the cart with the new promoCode.
    const currentCart = getShoppingCart(getState());
    await dispatch(updateCart(currentCart));
  } catch (error) {
    // On error, update cart with no promo code.
    // This handles the case where we had a valid promo code
    // and then entered an invalid one.
    if (promoCode) {
      dispatch({
        type: 'CHECKOUT/APPLY_PROMO_CODE',
        payload: { appliedPromoCode: '' }
      });
      await dispatch(applyPromoCode(''));
    }

    // then re-throw the original error.
    throw error;
  }
};

export const updateCartWithShipping = (useProductReviewApi = false) => (dispatch, getState) => {
  const currentCart = getShoppingCart(getState());
  return dispatch(updateCart(currentCart, useProductReviewApi));
};

export const validateShippingAddress = (info) => async (dispatch) => {
  const { userId, ...address } = info;
  try {
    const validAddress = await warehouse.verifyPaymentAddress(address);
    return dispatch({
      type: 'CHECKOUT/SET_VALID_ADDRESS',
      payload: validAddress
    });
  } catch (error) {
    dispatch(analytics.trackCheckoutAddressError({
      event: 'checkout_address_validation_service_error',
      field: '',
      error: error.toString(),
      userId
    }));
    return dispatch({
      type: 'CHECKOUT/NO_VALID_ADDRESS',
      payload: error
    });
  }
};

/**
 * Used for Packaged & PPO checkouts (not Merch).
 */
export const updateCartWithBilling = () => (dispatch, getState) => {
  const currentCart = getShoppingCart(getState());
  return dispatch(updateCartWithBillingInfo({ currentCart }));
};

/**
 * Fetches Cart for the logged in user from server.
 * Overwrites existing cart with received server data.
 *
 * Calling code should be catching Promise errors
 * and as neccessary handling in UI with feedback to user.
 */

export const fetchShoppingCart = () => async (dispatch, getState) => {
  // De-dupe concurrent requests.
  if (getIsFetchingCart(getState())) { return; }

  // If the cart has already been fetched, then there is no need to refetch.
  const currentCart = getShoppingCart(getState());
  if (currentCart !== ShoppingCart.defaultEmptyCart) {
    return;
  }

  // Fetch cart and convert to our internal Cart model.
  try {
    dispatch({
      type: SET_IS_FETCHING,
      payload: true
    });

    // Otherwise, fetch cart first time.
    const userIdOverride = queryString.parse(window.location.search).userId;
    const whCart = await warehouse.fetchShoppingCart({ userId: userIdOverride });
    const cart = ShoppingCart.createShoppingCartFromWarehouseResponse(whCart);

    // Dispatch warnings to display if present
    if (whCart.warning) {
      dispatch(setCartUpdateError(whCart.warning));
    }

    // For some reason, saved credit cards are returned
    // in the cart response. So hande that here.
    const savedCCs = whCart.previousCreditsCards;
    if (savedCCs && savedCCs.length > 0) {
      dispatch(updateSavedCreditCardsFromWHCartResponse(savedCCs));
    }

    // Add cart to state.
    return dispatch({
      type: 'CHECKOUT/SET_CART',
      payload: cart
    });
  } finally {
    dispatch({
      type: SET_IS_FETCHING,
      payload: false
    });
  }
};

/**
 * Returns Order metadata in format suitable
 * for API requests.
 */
const getAddressInfo = address => {
  const names = address.addressName.split(' ');

  const addressInfo = {
    city: address.city,
    country: address.country,
    first_name: (names.length > 0) ? names[0] : '',
    last_name: (names.length > 1) ? names[1] : '',
    phone: address.phoneNumber,
    postal: address.zip,
    state: address.state,
    street: address.streetAddress
  };

  return addressInfo;
};

export const getOrderSummaryInfo = (state) => {
  // Cart
  const cart = getShoppingCart(state);
  const displayedInfo = cart.toDisplayedInfo();
  const orderItems = cart.toOrderItems();

  // Shipping
  const shipping = getShippingFormFields(state);
  const shippingInfo = getAddressInfo(shipping);

  // Billing
  let billingInfo = getBillingSameAsShipping(state)
    ? shippingInfo
    : getAddressInfo(getBillingFormFields(state));

  billingInfo.phone = shippingInfo.phone;

  // Email compaigns may send users directly to cart.
  const UTMData = {};
  const UTMString = cookie.get('utm_string');
  if (UTMString) {
    UTMString.split('&').forEach(UTMElem => {
      if (UTMElem.startsWith('utm')) {
        const keyValue = UTMElem.split('=');
        UTMData[keyValue[0]] = keyValue[1];
      }
    });
  }

  return {
    orderItems,
    UTMData,
    displayedInfo,
    billingInfo,
    shippingInfo,
    payments: getSelectedPaymentState(state),
    code: getAppliedPromoCode(state),
    saveCC: getCreditCardFormFields(state).saveCC,
    selectedCC: getSelectedCreditCard(state)
  };
};

/**
 * Metadata from a successful order.
 */
export const orderSuccess = (orderInfo) => ({
  type: 'CHECKOUT/ORDER_SUCCESS',
  payload: orderInfo
});
